import { isBrowser } from '@/helpers/BrowserHelper';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { KCP_PAY_METHOD_TYPE } from '@/helpers/PaymentHelper';
import { useUserStore } from '@/stores/UserStore';

const HiddenForm = styled.form`
  display: none;
`;

/* KCP로 원복하는 것을 대비해서 argument는 이전 데이터 유지 */
interface KcpMobileInitParams {
  pgProc: string; // 프로세스 'bill' | 'pay'
  siteCd: string;
  siteName: string;
  siteLogo: string;
  groupId?: string;
  currency: string;
  approvalKey: string;
  traceNo: string;
  payUrl: string;
  retUrl: string;
  payMethod: KCP_PAY_METHOD_TYPE['mobile'];
  goodname: string;
  buyername: string;
  buyertel: string;
  buyeremail: string;
  price: number;
  oid: string;
  // custom
  completeUrl?: string;
  failUrl?: string;
  moduleYn?: string;
  gender?: string;
  missModuleCode?: string;
  missUserId?: string;
  solicitCode?: string;
}

export interface KcpMobileRef {
  init: (params: KcpMobileInitParams) => void;
}

/**
 * KCP 일시 결제 (모바일)
 */
const KcpMobileForm = forwardRef<KcpMobileRef>((props, ref) => {
  // 복제
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState<KcpMobileInitParams>();
  const userStore = useUserStore();

  // build 오류 방지
  if (typeof window === 'undefined') return;

  // KCP 결제창 호출
  const callPayForm = () => {
    if (!formRef.current) {
      alert('결제정보가 생성되지 않았습니다.');
      return;
    }
    const form = formRef.current;
    const PayUrl = (form.querySelector(
      'input[name="PayUrl"]',
    ) as HTMLInputElement).value;
    if (!PayUrl) {
      alert('결제에 필요한 필수 정부가 없습니다.');
      return;
    }

    // UTF-8 인코딩 처리
    const encodingTrans = 'UTF-8';
    if (encodingTrans === 'UTF-8') {
      form.action =
        PayUrl.substring(0, PayUrl.lastIndexOf('/')) +
        '/jsp/encodingFilter/encodingFilter.jsp';
    } else {
      form.action = PayUrl;
    }
    form.submit();
  };

  // 결제 시작
  const start = useCallback(
    (params: KcpMobileInitParams) => {
      const initFormData: KcpMobileInitParams = {
        pgProc: params.pgProc,
        siteCd: params.siteCd,
        siteName: params.siteName,
        siteLogo: params.siteLogo,
        groupId: params.groupId || '',
        currency: '410',
        approvalKey: params.approvalKey,
        traceNo: params.traceNo,
        payUrl: params.payUrl,
        retUrl: params.retUrl,
        payMethod: params.payMethod,
        goodname: params.goodname || '',
        buyername: params.buyername || '',
        buyertel: params.buyertel || '',
        buyeremail: params.buyeremail || '',
        price: params.price || 0,
        oid: params.oid || '',
        completeUrl: params.completeUrl || '',
        failUrl: params.failUrl || '',
        moduleYn: params.moduleYn || 'N',
        gender: params.gender || '',
        missModuleCode: params.missModuleCode || '',
        missUserId: params.missUserId || '',
        solicitCode: params.solicitCode || '',
      };
      setFormData(initFormData);
      callPayForm();
      console.log('KCP Mobile Payment Start', initFormData);
    },
    [callPayForm],
  );

  // ref로 넘길 함수
  useImperativeHandle(
    ref,
    () => ({
      init: start,
    }),
    [start],
  );

  return createPortal(
    <HiddenForm ref={formRef} method="post">
      {/* ************************* 가맹점 정보 설정 ************************* */}
      <input
        type="hidden"
        name="site_cd"
        value={formData?.siteCd || ''}
        readOnly
      />
      <input
        type="hidden"
        name="site_name"
        value={formData?.siteName || ''}
        readOnly
      />
      <input
        type="hidden"
        name="shop_name"
        value={formData?.siteName || ''}
        readOnly
      />
      <input
        type="hidden"
        name="site_logo"
        value={formData?.siteLogo || ''}
        readOnly
      />
      {/* 신용카드 : 최대 할부개월수 */}
      <input type="hidden" name="quotaopt" value="0" />
      {/* ************************* 결제정보 ************************* */}
      <input
        type="hidden"
        name="shop_user_id"
        value={userStore?.user?.donorId || ''}
        readOnly
        title="주문번호"
      />
      <input
        type="hidden"
        name="ordr_idxx"
        value={formData?.oid || ''}
        readOnly
        title="주문번호"
      />
      <input
        type="hidden"
        name="good_name"
        value={formData?.goodname || ''}
        readOnly
        title="상품명"
      />
      <input
        type="hidden"
        name="good_code"
        value={formData?.solicitCode || ''}
        readOnly
      />
      <input
        type="hidden"
        name="good_mny"
        value={formData?.price || 0}
        readOnly
        title="결제금액"
      />
      <input
        type="hidden"
        name="buyr_name"
        value={formData?.buyername || ''}
        readOnly
        title="구매자명"
      />
      <input
        type="hidden"
        name="buyr_tel2"
        value={formData?.buyertel || ''}
        readOnly
        title="구매자 연락처"
      />
      <input
        type="hidden"
        name="buyr_mail"
        value={formData?.buyeremail || ''}
        readOnly
        title="구매자 이메일"
      />
      {/*
		  			※ 결제 수단 정보 설정 
		  			신용카드 : CARD, 계좌이체 : BANK, 가상계좌 : VCNT
		  			포인트 : TPNT, 휴대폰 : MOBX, 상품권 : GIFT
		  			※ 필수
		  			 KCP에 신청된 결제수단으로만 결제가 가능합니다. 
          */}
      <input
        type="hidden"
        name="pay_method"
        value={formData?.payMethod || ''}
        readOnly
        title="결제수단"
      />
      <input
        type="hidden"
        name="currency"
        value={formData?.currency || '410'}
        readOnly
        title="결제 통화"
      />
      <input
        type="hidden"
        name="eng_flag"
        value={isBrowser && navigator.language.includes('ko') ? 'N' : 'Y'}
        readOnly
        title="영문 결제창 사용 여부"
      />
      <input
        type="hidden"
        name="approval_key"
        value={formData?.approvalKey || ''}
        readOnly
      />
      <input
        type="hidden"
        name="traceNo"
        value={formData?.traceNo || ''}
        readOnly
      />
      <input
        type="hidden"
        name="PayUrl"
        value={formData?.payUrl || ''}
        readOnly
      />
      {/* ************************* mobile only ************************* */}
      <input type="hidden" name="tablet_size" value="1.0" readOnly />
      <input type="hidden" name="encoding_trans" value="UTF-8" readOnly />
      <input type="hidden" name="req_tx" value="pay" readOnly />
      <input type="hidden" name="escw_used" value="N" readOnly />
      <input
        type="hidden"
        name="ActionResult"
        value={formData?.pgProc === 'bill' ? 'batch' : ''}
        readOnly
      />
      <input type="hidden" name="batch_cardno _return_yn" value="Y" readOnly />
      <input
        type="hidden"
        name="kcp_group_id"
        value={formData?.groupId || ''}
        readOnly
      />
      {/* Ret_URL : 콜백 URL */}
      <input
        type="hidden"
        name="Ret_URL"
        value={formData?.retUrl || ''}
        readOnly
      />
      {/* param_opt_1 : 완료시 이동 URL */}
      <input
        type="hidden"
        name="param_opt_1"
        value={formData?.completeUrl}
        readOnly
      />
      {/* param_opt_2 : 실패시 이동 URL */}
      <input
        type="hidden"
        name="param_opt_2"
        value={formData?.failUrl}
        readOnly
      />
      {/* param_opt_3 : 사용처 미정 */}
      <input type="hidden" name="param_opt_3" value="" readOnly />
      {/* van_code : 상품권, 포인트결제 필수값 */}
      <input type="hidden" name="van_code" value="" readOnly />
      {/* ************************* custom ************************* */}
      <input
        type="hidden"
        name="moduleYn"
        value={formData?.moduleYn || 'N'}
        readOnly
      />
      <input
        type="hidden"
        name="gender"
        value={formData?.gender || ''}
        readOnly
      />
      <input
        type="hidden"
        name="missModuleCode"
        value={formData?.missModuleCode || ''}
        readOnly
      />
      <input
        type="hidden"
        name="missUserId"
        value={formData?.missUserId || ''}
        readOnly
      />
      <input
        type="hidden"
        name="solicitCode"
        value={formData?.solicitCode || ''}
        readOnly
      />
      <input
        type="hidden"
        name="languageView"
        value={isBrowser && navigator.language.includes('ko') ? 'ko' : 'en'}
        readOnly
      />
    </HiddenForm>,
    document.body,
  );
});

export default KcpMobileForm;
